import { useCallback, useState } from 'react'
import type {
  CommunicationMethod,
  CreateScheduledCommunicationPayload,
  ScheduledCommunication,
  ScheduledCommunicationDetail,
  UpdateScheduledCommunicationPayload,
} from 'types/communications'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface ScheduledCommunicationsParams {
  skip?: number
  take?: number
  method?: CommunicationMethod
}

export function useScheduledCommunications(
  params?: ScheduledCommunicationsParams,
): QueryResult<ScheduledCommunication[]> {
  return useApiQuery<ScheduledCommunication[]>(`/api/scheduled-communications${qs(params)}`)
}

export function useScheduledCommunication(id: string | undefined): QueryResult<ScheduledCommunicationDetail> {
  return useApiQuery<ScheduledCommunicationDetail>(id ? `/api/scheduled-communications/${id}` : null)
}

export function useCreateScheduledCommunication(): MutationResult<
  ScheduledCommunication,
  [CreateScheduledCommunicationPayload]
> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateScheduledCommunicationPayload): Promise<ScheduledCommunication> => {
      setLoading(true)
      try {
        return await fetchApi<ScheduledCommunication>('/api/scheduled-communications', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateScheduledCommunication(): MutationResult<
  ScheduledCommunication,
  [string, UpdateScheduledCommunicationPayload]
> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateScheduledCommunicationPayload): Promise<ScheduledCommunication> => {
      setLoading(true)
      try {
        return await fetchApi<ScheduledCommunication>(`/api/scheduled-communications/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteScheduledCommunication(): MutationResult<ScheduledCommunication, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<ScheduledCommunication> => {
      setLoading(true)
      try {
        return await fetchApi<ScheduledCommunication>(`/api/scheduled-communications/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useAddScheduledCommunicationContact(): MutationResult<unknown, [string, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (scheduledCommunicationId: string, contactId: string): Promise<unknown> => {
      setLoading(true)
      try {
        return await fetchApi<unknown>(`/api/scheduled-communications/${scheduledCommunicationId}/contacts`, {
          method: 'POST',
          body: JSON.stringify({ contactId }),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useRemoveScheduledCommunicationContact(): MutationResult<unknown, [string, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (scheduledCommunicationId: string, contactId: string): Promise<unknown> => {
      setLoading(true)
      try {
        return await fetchApi<unknown>(
          `/api/scheduled-communications/${scheduledCommunicationId}/contacts/${contactId}`,
          { method: 'DELETE' },
        )
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useAddScheduledCommunicationContactGroup(): MutationResult<unknown, [string, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (scheduledCommunicationId: string, contactGroupId: string): Promise<unknown> => {
      setLoading(true)
      try {
        return await fetchApi<unknown>(`/api/scheduled-communications/${scheduledCommunicationId}/groups`, {
          method: 'POST',
          body: JSON.stringify({ contactGroupId }),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useRemoveScheduledCommunicationContactGroup(): MutationResult<unknown, [string, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (scheduledCommunicationId: string, contactGroupId: string): Promise<unknown> => {
      setLoading(true)
      try {
        return await fetchApi<unknown>(
          `/api/scheduled-communications/${scheduledCommunicationId}/groups/${contactGroupId}`,
          { method: 'DELETE' },
        )
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
